/**
 * Seletor de pessoa da EQUIPE deste tenant (pedido da PoolTrack, 05/10): o
 * "Responsável" listava os logins de portal das associações ("ABAM -
 * ASSOCIACAO…") misturados à equipe. A lista agora é `GET /users/team` — só
 * admin/atendente do tenant, nunca o portal, nunca outra empresa — e a UX é a
 * do "Técnico responsável" (SearchPicker): busca por nome ou e-mail, "Mostrar
 * inativos", a linha de baixo com o papel e o e-mail, "Mostrando os 30
 * primeiros — refine a busca" e o "Sem responsável".
 *
 * O valor traz o NOME de quem chama (o ticket já sabe quem é o responsável):
 * uma pessoa inativa já escolhida aparece certa com "Mostrar inativos"
 * desligado.
 */
import { useQuery } from '@tanstack/react-query';
import { UserRound } from 'lucide-react';
import { TEAM_PICKER_LIMIT, teamMemberSecondary } from '@erp/shared';
import {
  SearchPicker,
  type SearchPickerItem,
  type SearchPickerLabels,
  type SearchPickerValue,
  type UseSearchPickerResults,
} from '@/shared/SearchPicker';
import { teamSearchQuery } from './team-queries';

const useTeamResults: UseSearchPickerResults = ({ search, includeInactive, enabled }) => {
  const q = useQuery({ ...teamSearchQuery(search, includeInactive), enabled });
  return {
    items: (q.data?.items ?? []).map((m) => ({
      id: m.id,
      name: m.name,
      active: m.active,
      secondary: teamMemberSecondary(m),
    })),
    hasMore: q.data?.hasMore ?? false,
    isPending: q.isPending,
    isError: q.isError,
  };
};

const RESPONSIBLE_LABELS: SearchPickerLabels = {
  aria: 'Responsável',
  none: 'Sem responsável',
  clear: 'Sem responsável (limpar)',
  placeholder: 'Buscar por nome ou e-mail…',
  notFound: 'Ninguém da equipe com esse nome',
  empty: 'Nenhuma pessoa na equipe',
  error: 'Erro ao buscar a equipe',
};

export function TeamMemberPicker({
  value,
  onChange,
  disabled,
  compact = false,
  meId,
  labels,
}: {
  value: SearchPickerValue | null;
  onChange: (userId: number | null, item: SearchPickerItem | null) => void;
  disabled?: boolean;
  /** cabeçalho da visão compacta: gatilho baixo e estreito */
  compact?: boolean;
  /** quem está usando — a linha dele ganha "(eu)" */
  meId?: number;
  /** padrão: os do "Responsável" */
  labels?: Partial<SearchPickerLabels>;
}) {
  return (
    <SearchPicker
      value={value}
      onChange={onChange}
      useResults={useTeamResults}
      labels={{ ...RESPONSIBLE_LABELS, ...labels }}
      icon={UserRound}
      limit={TEAM_PICKER_LIMIT}
      disabled={disabled}
      compact={compact}
      meId={meId}
    />
  );
}
